import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { LearningPlanBuilderDataService } from '../../services/learning-plan-builder-data.service';
import { map, Subject, takeUntil } from 'rxjs';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { startWith } from 'rxjs/operators';
import { LearningPlanStructureComponent } from '../learning-plan-structure/learning-plan-structure.component';
import { PhaseUiModel } from '../../models/LearningPlanInstanceDetailUIModel';

interface DisplaySettings {
  label: string;
  value: string;
}

@Component({
  selector: 'app-learning-plan-builder-overview',
  imports: [TranslocoDirective, LearningPlanStructureComponent],
  templateUrl: './learning-plan-builder-overview.component.html',
})
export class LearningPlanBuilderOverviewComponent implements OnInit, OnDestroy {
  readonly translocoService = inject(TranslocoService);
  readonly dataService = inject(LearningPlanBuilderDataService);

  private readonly destroy$: Subject<void> = new Subject();
  protected displaySettings: DisplaySettings[] = [];
  protected displayPhases = signal<PhaseUiModel[]>([]);
  protected arrowChangesTrigger = signal(0);

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.dataService.formGroupSettings.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        startWith(this.dataService.formGroupSettings.getRawValue()),
        map((rawSettings) => [
          {
            label:
              'portal.trainings.builder.stepper.settings.form.trainingName.label',
            value: rawSettings.learningPlanName ?? '',
          },
          {
            label:
              'portal.trainings.builder.stepper.settings.form.phaseFrequency.label',
            value: this.getPhaseFrequency(rawSettings.duration ?? 'year'),
          },
          {
            label:
              'portal.trainings.builder.stepper.settings.form.startDate.label',
            value: this.transformDateForDisplay(
              rawSettings.startDate ??
                new Date(new Date().getFullYear(), new Date().getMonth(), 1),
            ),
          },
        ]),
      )
      .subscribe((value) => (this.displaySettings = value));

    const displayPhases$ =
      this.dataService.formGroupCourseSelection.valueChanges.pipe(
        takeUntil(this.destroy$),
        startWith(this.dataService.formGroupCourseSelection.getRawValue()),
        map((rawCourseSelection) =>
          rawCourseSelection.phases
            ? rawCourseSelection.phases.map((phase, index) => ({
                id: `${index}`,
                name: `${index}`,
                duration:
                  this.dataService.formGroupSettings.controls.duration.value ??
                  '',
                nextPhaseId: null,
                courseInstances: [
                  {
                    id: '',
                    courseId: phase.course?.id ?? '',
                    name: phase.course?.name ?? '',
                    aboutUrl: phase.course?.aboutUrl ?? '',
                  },
                ],
              }))
            : [],
        ),
      );

    displayPhases$.subscribe((phases) => {
      this.displayPhases.set(phases);
      this.arrowChangesTrigger.update((current) => current + 1);
    });
  }

  transformDateForDisplay(date?: Date | null) {
    if (!date) {
      return '';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}.${month}.${date.getFullYear()}`;
  }

  getPhaseFrequency(phaseFrequencyId?: string | null) {
    return phaseFrequencyId
      ? this.translocoService.translate(
          `portal.trainings.builder.stepper.settings.form.phaseFrequency.options.${phaseFrequencyId}`,
        )
      : '';
  }
}
